/* eslint-disable no-magic-numbers */
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { i18n } from "@/i18n";
import { surveyAPI, teamAPI, meetingAPI, goalAPI } from "@/api";
import moment from "moment-timezone";
import keyBy from "lodash/keyBy";
import orderBy from "lodash/orderBy";
import round from "lodash/round";
import sample from "lodash/sample";
import random from "lodash/random";
import take from "lodash/take";
import remove from "lodash/remove";
import { sortGoals } from "@/lib/goalUtil";
import teamWorkStyles from "@/components/Team/teamWorkStyles";
import TeamOverviewMeetingFilter from "@/constants/TeamOverviewMeetingFilter";
import TeamRoleType from "@/constants/TeamRoleType";
import GoalType from "@/constants/GoalType";

const GOAL_PAGE_LIMIT = 10;

const pristineSurveySummary = () => {
	return {
		date: "",
		questions: [],
		responders: {},
		isPublished: false,
		isInProgress: false,
	};
};

const pristineTeam = () => {
	return {
		members: [],
	};
};

const pristineState = () => {
	return {
		teamId: "",
		userProfile: {},
		timezone: "",
		team: pristineTeam(),

		// flags
		isInitializingStore: true,
		isStoreInitialized: false,
		isFetchingTeam: true,
		isFetchingRecentDecisions: true,
		isFetchingTeamHealthSurveySummary: true,
		isFetchingTeamMeetings: true,
		isFetchingTeamGoals: true,

		// recent decisions
		recentDecisionsFilterValue: "",
		recentDecisions: [],

		// team health
		teamHealthSurveySummary: pristineSurveySummary(),

		// team meetings
		activeMeetingFilter: TeamOverviewMeetingFilter.UPCOMING,
		recentMeetings: [],
		upcomingMeetings: [],

		// team goals
		goals: [],
		goalPageOffset: 0,
		hasMoreGoalPages: true,
		hasPagedGoals: false,
	};
};

const _state = pristineState();

const _getters = {
	// flags
	isInitializingStore: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isInitializingStore;
	},
	isFetchingTeam: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingTeam;
	},
	isFetchingRecentDecisions: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingRecentDecisions;
	},
	isFetchingTeamHealthSurveySummary: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingTeamHealthSurveySummary;
	},
	isFetchingTeamMeetings: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingTeamMeetings;
	},
	isFetchingTeamGoals: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingTeamGoals;
	},

	// general
	timezone: ( state /* getters, rootState, rootGetters */ ) => {
		return state.timezone;
	},
	team: ( state /* getters, rootState, rootGetters */ ) => {
		return state.team;
	},
	members: ( state /* getters, rootState, rootGetters */ ) => {
		return ( state.team?.members || [] ).map( member => {
			return {
				...member,
				isTeamLead: member.role === TeamRoleType.LEAD,
			};
		} );
	},
	leads: ( state, getters /* rootState, rootGetters */ ) => {
		return getters.members.filter( member => {
			return member.isTeamLead;
		} );
	},
	myTeamMember: ( state, getters /* rootState, rootGetters */ ) => {
		const { id: personId, } = state.userProfile;
		return getters.members.find( member => {
			return member.personId === personId;
		} );
	},

	// recent decisions
	recentDecisionFilterOptions: ( state /* getters, rootState, rootGetters */ ) => {
		const { timezone, } = state;
		const now = moment.tz( timezone );
		const nowLessSeven = now.clone().subtract( 7, "days" ).format( "YYYY-MM-DD" );
		const nowLessFourteen = now.clone().subtract( 14, "days" ).format( "YYYY-MM-DD" );
		const nowLessThirty = now.clone().subtract( 30, "days" ).format( "YYYY-MM-DD" );
		return [
			{ value: "7", date: nowLessSeven, },
			{ value: "14", date: nowLessFourteen, },
			{ value: "30", date: nowLessThirty, },
		].map( option => {
			option.text = i18n.t( "teamOverviewStore.filterDaysLabel", {
				days: option.value,
			} );
			return option;
		} );
	},
	defaultFilterOption: ( state /* getters, rootState, rootGetters */ ) => {
		const { timezone, } = state;
		const now = moment.tz( timezone );
		const nowPlusSeven = now.clone().add( 7, "days" ).format( "YYYY-MM-DD" );
		return {
			value: "7",
			text: i18n.t( "teamOverviewStore.filterDaysLabel", {
				days: "7",
			} ),
			date: nowPlusSeven,
		};
	},
	recentDecisionsFilterValue: ( state /* getters, rootState, rootGetters */ ) => {
		return state.recentDecisionsFilterValue;
	},
	recentDecisions: ( state /* getters, rootState, rootGetters */ ) => {
		return state.recentDecisions;
	},

	// team health
	teamHealthSurveySummary: ( state /* getters, rootState, rootGetters */ ) => {
		return state.teamHealthSurveySummary;
	},

	// team dynamic
	completedWorkStyleAssessmentCount: ( state, getters /* , rootState, rootGetters */ ) => {
		return getters.members.reduce( ( count, member ) => {
			count += ( "DISC" in member.assessments ) ? 1 : 0;
			return count;
		}, 0 );
	},
	workStylePercentages: ( state, getters /* rootState, rootGetters */ ) => {
		const initialCounts = {
			D: 0,
			I: 0,
			S: 0,
			C: 0,
			total: 0,
		};

		const allCounts = getters.members
			.filter( member => !!member.assessments?.DISC )
			.reduce( ( accumulatedCounts, member ) => {
				const letter = member.assessments.DISC.personalities.disc_type[ 0 ].toUpperCase();
				accumulatedCounts[ letter ] += 1;
				accumulatedCounts.total += 1;
				return accumulatedCounts;
			}, initialCounts );

		if ( allCounts.total === 0 ) {
			delete allCounts.total;
			return allCounts;
		}

		return {
			D: round( allCounts.D / allCounts.total, 2 ),
			I: round( allCounts.I / allCounts.total, 2 ),
			S: round( allCounts.S / allCounts.total, 2 ),
			C: round( allCounts.C / allCounts.total, 2 ),
		};
	},
	strengthAndBlindSpot: ( state, getters /* rootState, rootGetters */ ) => {
		const order = {
			D: 0,
			I: 1,
			S: 2,
			C: 3,
		};
		const percentages = getters.workStylePercentages;
		const letters = Object.keys( percentages )
			.filter( letter => !!percentages[ letter ] );
		const sortedLetters = orderBy( letters, ( letter => order[ letter ] ) );
		const mergedLetters = sortedLetters.join( "" ).toLowerCase();
		if ( !mergedLetters.length ) {
			return {
				strength: "",
				blindSpot: "",
				prioritize: "",
			};
		}
		const { strengths, blindSpots, } = teamWorkStyles[ mergedLetters ];
		return {
			strength: sample( strengths ),
			blindSpot: sample( blindSpots ),
			prioritize: random( 1, 2 ) === 1 ? "strength" : "blindSpot",
		};
	},
	membersByMoniker: ( state, getters /* rootState, rootGetters */ ) => {
		const initialGroups = {
			D: [],
			I: [],
			S: [],
			C: [],
		};
		return getters.members.filter( member => !!member.assessments?.DISC )
			.reduce( ( groups, member ) => {
				const { moniker, } = member.assessments.DISC;
				const letter = moniker[ 0 ].toUpperCase();
				groups[ letter ].push( { ...member, } );
				return groups;
			}, initialGroups );
	},

	// meetings
	recentMeetings: ( state /* getters, rootState, rootGetters */ ) => {
		return take( orderBy( state.recentMeetings, [ "plannedStart", ], [ "desc", ] ), 5 );
	},
	upcomingMeetings: ( state /* getters, rootState, rootGetters */ ) => {
		return take( orderBy( state.upcomingMeetings, [ "plannedStart", ], [ "asc", ] ), 5 );
	},
	filteredMeetings: ( state, getters /* rootState, rootGetters */ ) => {
		return getters.activeMeetingFilter === TeamOverviewMeetingFilter.UPCOMING ?
			getters.upcomingMeetings :
			getters.recentMeetings;
	},
	activeMeetingFilter: ( state /* getters, rootState, rootGetters */ ) => {
		return state.activeMeetingFilter;
	},

	// goals
	goals: ( state /* getters, rootState, rootGetters */ ) => {
		return sortGoals( state.goals, "asc" );
	},
	hasMoreGoalPages: ( state /* getters, rootState, rootGetters */ ) => {
		return state.hasMoreGoalPages;
	},
	hasPagedGoals: ( state /* getters, rootState, rootGetters */ ) => {
		return state.hasPagedGoals;
	},
};

const mutations = {
	toggleFlag: ( state, { flag, value, } ) => {
		state[ flag ] = value;
	},
	onStoreInitialized: ( state, { initializedState, } ) => {
		Object.assign( state, initializedState );
	},
	onTeamFetched: ( state, { team, } ) => {
		state.team = team;
	},

	// recent decisions
	onRecentDecisionFilterValueChanged: ( state, { filterValue, } ) => {
		state.recentDecisionsFilterValue = filterValue;
	},
	onRecentDecisionsChanged: ( state, { recentDecisions, } ) => {
		state.recentDecisions = recentDecisions;
	},

	// team health
	onTeamHealthSurveySummaryFetched: ( state, { teamHealthSurveySummary, } ) => {
		state.teamHealthSurveySummary = teamHealthSurveySummary;
	},

	// meetings
	onMeetingsFetched: ( state, { recentMeetings, upcomingMeetings, } ) => {
		state.recentMeetings = recentMeetings;
		state.upcomingMeetings = upcomingMeetings;
	},
	onMeetingFilterChanged: ( state, { meetingFilter, } ) => {
		state.activeMeetingFilter = meetingFilter;
	},

	// goals
	onTeamGoalsFetched: ( state, { teamGoals, } ) => {
		state.goals = [
			...state.goals,
			...teamGoals,
		];
		state.goalPageOffset += teamGoals.length;
		state.hasMoreGoalPages = teamGoals.length === GOAL_PAGE_LIMIT;
		state.hasPagedGoals = true;
	},
};

const actions = {
	initializeStore: async ( { commit, dispatch, /* state, rootGetters, rootState, */ }, { teamId, userProfile, timezone, } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isInitializingStore", value: true, } );
			const initializedState = Object.assign( pristineState(), {
				teamId,
				userProfile,
				timezone,
			} );
			commit( "onStoreInitialized", { initializedState, } );
			await dispatch( "fetchTeam" );
			dispatch( "fetchRecentDecisions", { filterValue: "7", } );
			dispatch( "fetchTeamHealthSurvey" );
			dispatch( "fetchTeamMeetings" );
			dispatch( "fetchTeamGoals" );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.initializingStoreError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isInitializingStore", value: false, } );
		}
	},

	fetchTeam: async ( { commit, state, /* dispatch, rootGetters, rootState, */ } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingTeam", value: true, } );
			const { teamId, } = state;
			const team = await teamAPI.fetchTeam( { teamId, } );
			commit( "onTeamFetched", { team, } );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.fetchTeamError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isFetchingTeam", value: false, } );
		}
	},

	//
	// recent decisions
	//
	fetchRecentDecisions: async ( { commit, state, getters, /* dispatch, rootGetters, rootState, */ }, { filterValue, } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingRecentDecisions", value: true, } );
			const { teamId, recentDecisionsFilterValue, timezone, } = state;
			if ( recentDecisionsFilterValue === filterValue ) {
				return;
			}

			const { recentDecisionFilterOptions, defaultFilterOption, } = getters;
			const selectedFilterOption = recentDecisionFilterOptions
				.find( filterOption => filterOption.value === filterValue ) ||
				defaultFilterOption;

			const { date: createdAfter, } = selectedFilterOption;

			const { outcomes, meetings, } = await teamAPI.fetchRecentDecisions( teamId, { createdAfter, } );

			const meetingMap = keyBy( meetings, "id" );

			const recentDecisions = outcomes.map( outcome => {
				const meeting = meetingMap[ outcome.meetingId ] || {
					name: "Unknown",
				};
				return {
					...outcome,
					formattedCreatedDate: moment( outcome.created * 1000 ).tz( timezone ).format( "dddd, MMMM D" ),
					meetingTitle: meeting.title,
					isMeetingInvitee: meeting.isInvitee,
				};
			} );

			commit( "onRecentDecisionFilterValueChanged", { filterValue, } );
			commit( "onRecentDecisionsChanged", { recentDecisions, } );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.fetchRecentDecisionsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isFetchingRecentDecisions", value: false, } );
		}
	},

	// team health
	fetchTeamHealthSurvey: async ( { commit, state, /* dispatch, rootGetters, rootState, */ } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingTeamHealthSurveySummary", value: true, } );

			const { teamId, timezone, } = state;

			// get the latest published survey
			const allSurveys = await surveyAPI.fetchTeamSurveys( { teamId, } );
			const orderedSurveys = orderBy(
				( allSurveys || [] ),
				[ "created", "published", ], [ "desc", "desc", ]
			);

			if ( !orderedSurveys.length ) {
				commit( "onTeamHealthSurveySummaryFetched", { teamHealthSurveySummary: pristineSurveySummary(), } );
				return;
			}

			const lastSurvey = orderedSurveys[ 0 ];
			const lastPublishedResults = lastSurvey.result;

			// then we need to get its template
			const { surveyTemplateId: templateId, responders, } = lastSurvey;
			const template = await surveyAPI.fetchSurveyTemplate( { templateId, } );

			// now we need to format le data
			const orderedQuestions = orderBy(
				template.questions,
				[ "sortOrder", ], [ "asc", ]
			);

			const surveySummaryQuestions = orderedQuestions.map( question => {
				const { id: questionId, titleKey, sortOrder, choices, } = question;
				const title = i18n.t( `teamOverviewStore.${ titleKey }` );
				const choiceCount = choices.length;
				let average = 0;
				if ( questionId in lastPublishedResults ) {
					average = lastPublishedResults[ questionId ].average;
				}
				return {
					questionId,
					title,
					sortOrder,
					choiceCount,
					average,
				};
			} );

			const teamHealthSurveySummary = {
				date: moment.unix( lastSurvey.created ).tz( timezone ).format( "MMM D" ), // e.g., Jan 3
				questions: surveySummaryQuestions,
				responders: { ...responders, },
				isPublished: !!lastSurvey.published,
				isInProgress: !lastSurvey.published,
			};

			commit( "onTeamHealthSurveySummaryFetched", { teamHealthSurveySummary, } );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.fetchTeamHealthSurveyError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isFetchingTeamHealthSurveySummary", value: false, } );
		}
	},

	// meetings
	fetchTeamMeetings: async ( { commit, state, getters, /* dispatch, rootGetters, rootState, */ } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingTeamMeetings", value: true, } );

			const { teamId, timezone, } = state;
			const [ recentMeetings, upcomingMeetings, ] = await Promise.all( [
				meetingAPI.fetchRecentTeamMeetings( { teamId, timezone, } ),
				meetingAPI.fetchUpcomingTeamMeetings( { teamId, timezone, } ),
			] ).then( responses => {
				const recent = responses[ 0 ].value;
				const upcoming = responses[ 1 ].value;

				// if there's any recent meeting "in progress", move it to the upcoming
				// collection instead
				const now = moment.tz( timezone );
				const adjustedUpcoming = upcoming.concat(
					remove( recent, recentMeeting => {
						const plannedStart = moment.unix( recentMeeting.plannedStart ).tz( timezone );
						const plannedEnd = moment.unix( recentMeeting.plannedEnd ).tz( timezone );
						return now.isBetween( plannedStart, plannedEnd );
					} )
				);

				// mark the invitees who are team leads and organizers, for use in components
				const { leads, } = getters;
				const leadsById = keyBy( leads, "personId" );

				for ( const meeting of recent ) {
					for ( const invitee of meeting.invitees ) {
						invitee.isTeamLead = !!leadsById[ invitee.personId ];
						invitee.isOrganizer = invitee.personId === meeting.organizer.personId;
					}
				}

				for ( const meeting of adjustedUpcoming ) {
					for ( const invitee of meeting.invitees ) {
						invitee.isTeamLead = !!leadsById[ invitee.personId ];
						invitee.isOrganizer = invitee.personId === meeting.organizer.personId;
					}
				}

				return [
					recent, adjustedUpcoming,
				];
			} );
			commit( "onMeetingsFetched", { recentMeetings, upcomingMeetings, } );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.fetchTeamMeetingsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isFetchingTeamMeetings", value: false, } );
		}
	},
	filterMeetings: async ( { commit, /* state, dispatch, rootGetters, rootState, */ }, { meetingFilter, } ) => {
		commit( "onMeetingFilterChanged", { meetingFilter, } );
	},

	// goals
	fetchTeamGoals: async ( { commit, state, /* dispatch, rootGetters, rootState, */ } ) => {
		try {
			const { teamId, goalPageOffset, hasMoreGoalPages, } = state;

			if ( !hasMoreGoalPages ) {
				return;
			}

			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingTeamGoals", value: true, } );

			const goalTypes = [ GoalType.ACTIVE, ];
			const teamGoals = await goalAPI.fetchTeamGoals( {
				teamId,
				goalTypes,
				limit: GOAL_PAGE_LIMIT,
				offset: goalPageOffset,
			} );

			commit( "onTeamGoalsFetched", { teamGoals, } );
		} catch ( e ) {
			const message = i18n.t( "teamOverviewStore.fetchGoalsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isFetchingTeamGoals", value: false, } );
		}
	},
};

export default {
	state: _state,
	getters: _getters,
	mutations,
	actions,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamOverview/index.js